import { projectsId, type ProjectsId } from "@papier/projects-schema/db_gen/public/Projects";
import {
  environmentsId,
  type EnvironmentsId,
} from "@papier/environments-schema/db_gen/public/Environments";
import {
  templateType,
  type default as TemplateType,
} from "@papier/templates-schema/db_gen/public/TemplateType";
import { notificationStatus, type default as NotificationStatus } from "./NotificationStatus";
import {
  templateContLocaleId,
  type TemplateContLocaleId,
} from "@papier/templates-schema/db_gen/public/TemplateContLocale";
import {
  organizationsId,
  type OrganizationsId,
} from "@papier/auth-schema/db_gen/public/Organizations";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for logs */
export type LogsId = string & { __flavor: "LogsId" };

/**
 * Represents the table public.logs
 * @uniqueConstraints {"logs_created_at_idx":{"name":"logs_created_at_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"logs_environment_id_idx":{"name":"logs_environment_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"environment_id","definition":"environment_id","predicate":null}]},"logs_from_idx":{"name":"logs_from_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"from","definition":"\"from\"","predicate":null}]},"logs_project_id_idx":{"name":"logs_project_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"logs_recipient_idx":{"name":"logs_recipient_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"recipient","definition":"recipient","predicate":null}]},"logs_recipient_type_idx":{"name":"logs_recipient_type_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"recipient_type","definition":"recipient_type","predicate":null}]},"logs_sent_at_idx":{"name":"logs_sent_at_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"sent_at","definition":"sent_at","predicate":null}]},"logs_status_idx":{"name":"logs_status_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"status","definition":"status","predicate":null}]},"logs_template_content_locale_id_idx":{"name":"logs_template_content_locale_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"template_content_locale_id","definition":"template_content_locale_id","predicate":null}]},"notification_logs_pkey":{"name":"notification_logs_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]}}
 */
export default interface LogsTable {
  id: ColumnType<LogsId, LogsId | undefined, LogsId>;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  environmentId: ColumnType<EnvironmentsId, EnvironmentsId, EnvironmentsId>;

  recipientType: ColumnType<TemplateType, TemplateType, TemplateType>;

  recipient: ColumnType<string, string, string>;

  status: ColumnType<NotificationStatus, NotificationStatus | undefined, NotificationStatus>;

  errorMessage: ColumnType<string | null, string | null, string | null>;

  variables: ColumnType<unknown | null, unknown | null, unknown | null>;

  rawPayload: ColumnType<unknown | null, unknown | null, unknown | null>;

  createdAt: ColumnType<Date, Date | undefined, Date>;

  sentAt: ColumnType<Date | null, Date | null, Date | null>;

  providerId: ColumnType<string | null, string | null, string | null>;

  providerName: ColumnType<string | null, string | null, string | null>;

  templateContentLocaleId: ColumnType<
    TemplateContLocaleId,
    TemplateContLocaleId,
    TemplateContLocaleId
  >;

  from: ColumnType<string, string, string>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId | undefined, OrganizationsId>;
}

export type Logs = Selectable<LogsTable>;

export type NewLogs = Insertable<LogsTable>;

export type LogsUpdate = Updateable<LogsTable>;

export const logsId = z.string() as unknown as z.ZodType<LogsId, LogsId>;

export const logs = z.object({
  id: z.lazy(() => logsId),
  projectId: z.lazy(() => projectsId),
  environmentId: z.lazy(() => environmentsId),
  recipientType: templateType,
  recipient: z.string(),
  status: notificationStatus,
  errorMessage: z.string().nullable(),
  variables: z.unknown().nullable(),
  rawPayload: z.unknown().nullable(),
  createdAt: z.date(),
  sentAt: z.date().nullable(),
  providerId: z.string().nullable(),
  providerName: z.string().nullable(),
  templateContentLocaleId: z.lazy(() => templateContLocaleId),
  from: z.string(),
  organizationId: z.lazy(() => organizationsId),
});

export const logsInitializer = z.object({
  id: z.lazy(() => logsId).optional(),
  projectId: z.lazy(() => projectsId),
  environmentId: z.lazy(() => environmentsId),
  recipientType: templateType,
  recipient: z.string(),
  status: notificationStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  variables: z.unknown().optional().nullable(),
  rawPayload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  sentAt: z.date().optional().nullable(),
  providerId: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
  templateContentLocaleId: z.lazy(() => templateContLocaleId),
  from: z.string(),
  organizationId: z.lazy(() => organizationsId).optional(),
});

export const logsMutator = z.object({
  id: z.lazy(() => logsId).optional(),
  projectId: z.lazy(() => projectsId).optional(),
  environmentId: z.lazy(() => environmentsId).optional(),
  recipientType: templateType.optional(),
  recipient: z.string().optional(),
  status: notificationStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  variables: z.unknown().optional().nullable(),
  rawPayload: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  sentAt: z.date().optional().nullable(),
  providerId: z.string().optional().nullable(),
  providerName: z.string().optional().nullable(),
  templateContentLocaleId: z.lazy(() => templateContLocaleId).optional(),
  from: z.string().optional(),
  organizationId: z.lazy(() => organizationsId).optional(),
});

// ── constraints for Logs ─────────────────────────────────
export const LogsConstraints = {
  logs_created_at_idx: {
    name: "logs_created_at_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  logs_environment_id_idx: {
    name: "logs_environment_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "environment_id", definition: "environment_id", predicate: null }],
  },
  logs_from_idx: {
    name: "logs_from_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "from", definition: '"from"', predicate: null }],
  },
  logs_project_id_idx: {
    name: "logs_project_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  logs_recipient_idx: {
    name: "logs_recipient_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "recipient", definition: "recipient", predicate: null }],
  },
  logs_recipient_type_idx: {
    name: "logs_recipient_type_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "recipient_type", definition: "recipient_type", predicate: null }],
  },
  logs_sent_at_idx: {
    name: "logs_sent_at_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "sent_at", definition: "sent_at", predicate: null }],
  },
  logs_status_idx: {
    name: "logs_status_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "status", definition: "status", predicate: null }],
  },
  logs_template_content_locale_id_idx: {
    name: "logs_template_content_locale_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [
      {
        name: "template_content_locale_id",
        definition: "template_content_locale_id",
        predicate: null,
      },
    ],
  },
  notification_logs_pkey: {
    name: "notification_logs_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
} as const;
export type LogsConstraintsType = typeof LogsConstraints;
